import React, { useEffect, useState, useRef } from 'react';
import { useDispatch } from 'react-redux';
import html2pdf from 'html2pdf.js';
import {
    BarChart,
    Bar,
    XAxis,
    YAxis,
    CartesianGrid,
    Tooltip,
    ResponsiveContainer,
    Legend
} from "recharts";
import { fetchBloodPressureReport } from "./store";
import { Card, Table, Form, Button, FormControl, Modal } from 'react-bootstrap';
import { FaHeartbeat, FaSearch, FaDownload, FaTimes, FaUser, FaVenusMars, FaMobileAlt, FaLayerGroup, FaDotCircle, FaChevronLeft, FaChevronRight, FaChevronUp, FaChevronDown } from "react-icons/fa";
import { MdTrendingUp } from "react-icons/md";
import Swal from 'sweetalert2';
import { ComponentLoader } from '../common/Loader';

const BloodPressureReport = () => {
    const dispatch = useDispatch();
    const [reportData, setReportData] = useState({
        summary: { totalReadings: null, avgSystolic: null, avgDiastolic: null, avgPulse: null },
        tableData: [],
        barChartData: [],
        lineChartData: []
    });

    // Pagination State
    const [totalCount, setTotalCount] = useState(0);
    const [itemsPerPage, setItemsPerPage] = useState(10);
    const [loading, setLoading] = useState(false);
    const [currentPage, setCurrentPage] = useState(1);
    const [goToPage, setGoToPage] = useState('');
    const [sortBy, setSortBy] = useState('testDate:desc');


    // Export State
    const [showExportModal, setShowExportModal] = useState(false);
    const [exportOption, setExportOption] = useState("currentPage"); // "currentPage", "allPages"
    const [isExporting, setIsExporting] = useState(false);

    const initialFilters = {
        gender: 'All',
        ageRange: 'All',
        deviceType: 'All',
        category: 'All',
        systolicRange: 'All',
        diastolicRange: 'All',
        searchTerm: '',
        startDate: '',
        endDate: ''
    };

    const [filters, setFilters] = useState(initialFilters);
    const [prevFilters, setPrevFilters] = useState('');
    const [debouncedSearch, setDebouncedSearch] = useState('');
    const searchTimeoutRef = useRef(null);
    const isFetching = useRef(false);
    const lastFetchParams = useRef(null);

    const fetchData = (isPageChange = false) => {
        if (isFetching.current) return;
        setLoading(true);
        isFetching.current = true;
        
        const currentFilterSet = JSON.stringify({ ...filters, debouncedSearch, sortBy });
        const skipCharts = isPageChange && currentFilterSet === prevFilters;

        const payload = {
            ...filters,
            search: debouncedSearch,
            page: currentPage, 
            limit: itemsPerPage,
            skipCharts,
            sortBy: sortBy.split(':')[0],
            sortOrder: sortBy.split(':')[1]
        };

        dispatch(fetchBloodPressureReport(payload))
            .then((response) => {
                if (response.payload && response.payload.success) {
                    const newData = response.payload.tableData || [];
                    
                    if (skipCharts) {
                        setReportData(prev => ({
                            ...prev,
                            tableData: newData
                        }));
                    } else {
                        setReportData(response.payload);
                        setPrevFilters(currentFilterSet);
                    }
                    
                    setTotalCount(response.payload.totalCount || 0);
                }
                setLoading(false);
                isFetching.current = false;
            })
            .catch((error) => {
                console.error('Error fetching BP report:', error);
                setLoading(false);
                isFetching.current = false;
            });
    };

    useEffect(() => {
        const currentParams = JSON.stringify({ ...filters, debouncedSearch, sortBy, currentPage, itemsPerPage });
        if (lastFetchParams.current === currentParams) return; 
        lastFetchParams.current = currentParams;

        const currentFilterSet = JSON.stringify({ ...filters, debouncedSearch, sortBy });
        const isPageChange = currentFilterSet === prevFilters;
        fetchData(isPageChange);
    }, [dispatch, debouncedSearch, filters.gender, filters.ageRange, filters.deviceType, filters.category, filters.systolicRange, filters.diastolicRange, filters.startDate, filters.endDate, sortBy, currentPage, itemsPerPage]);

    const handleGoToPage = () => {
        const maxPages = Math.ceil(totalCount / itemsPerPage);
        const pageNum = parseInt(goToPage, 10);
        if (!pageNum || pageNum < 1 || pageNum > maxPages) {
            Swal.fire('Invalid Page', `Total pages: ${maxPages}`, 'warning');
            return;
        }
        setCurrentPage(pageNum);
        setGoToPage('');
    };

    const handleFilterChange = (field, value) => {
        setFilters(prev => ({ ...prev, [field]: value }));
        setCurrentPage(1);
    };

    const handleSearchChange = (e) => {
        const val = e.target.value;
        setFilters(prev => ({ ...prev, searchTerm: val }));

        if (searchTimeoutRef.current) clearTimeout(searchTimeoutRef.current);
        searchTimeoutRef.current = setTimeout(() => {
            setDebouncedSearch(val);
            setCurrentPage(1);
        }, 500);
    };

    const handleItemsPerPageChange = (newVal) => {
        setItemsPerPage(Number(newVal));
        setCurrentPage(1);
    };

    const clearFilters = () => {
        setFilters(initialFilters);
        setDebouncedSearch('');
        setCurrentPage(1);
    };

    const formatTime = (dateStr) => {
        if (!dateStr) return "—";
        const date = new Date(dateStr);
        return date.toLocaleTimeString('en-IN', { hour: 'numeric', minute: '2-digit', hour12: true }).toUpperCase();
    };

    const formatDate = (dateStr) => {
        if (!dateStr) return "—";
        const date = new Date(dateStr);
        return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
    };

    const generateCSV = (data) => {
        if (!data || data.length === 0) return;

        const headers = ["User ID", "Gender", "Age", "Systolic", "Diastolic", "Pulse", "Category", "Reading Date", "Reading Time", "Device"];
        const rows = data.map(item => [
            item.userId || "—",
            item.userGender || "—",
            item.userAge || "—",
            item.systolic || "—",
            item.diastolic || "—",
            item.pulse || "—",
            item.category || "—",
            formatDate(item.testDate),
            formatTime(item.testDate),
            item.userDeviceType === 'A' ? 'Android' : item.userDeviceType === 'I' ? 'iOS' : 'Web'
        ]);

        const csvContent = "data:text/csv;charset=utf-8,"
            + headers.join(",") + "\n"
            + rows.map(e => e.map(cell => `"${String(cell).replace(/"/g, '""')}"`).join(",")).join("\n");

        const encodedUri = encodeURI(csvContent);
        const link = document.createElement("a");
        link.setAttribute("href", encodedUri);
        link.setAttribute("download", `BP_Report_Export_${new Date().toISOString().split('T')[0]}.csv`);
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
    };

    const generatePDF = (data) => {
        if (!data || data.length === 0) return;
    
        const totalRecords = data.length;
        const generatedDate = new Date().toLocaleDateString('en-GB');
        const generatedTime = new Date().toLocaleTimeString('en-US', { hour12: false });
    
        const rowsPerPage = 8;
        const totalTablePages = Math.ceil(totalRecords / rowsPerPage);
        const totalPDFPages = 1 + totalTablePages; 
    
        const styles = `
            <style>
                @import url('https://fonts.googleapis.com/css2?family=Poppins:wght@300;400;500;600;700;800&display=swap');
                body { font-family: 'Poppins', sans-serif; color: #1e293b; margin: 0; padding: 0; }
                .report-container { min-height: 275mm; display: flex; flex-direction: column; background: #fff; padding: 40px 30px 20px; box-sizing: border-box; page-break-after: always; }
                .exp-page-header { display: flex; justify-content: space-between; align-items: flex-start; margin-bottom: 40px; }
                .brand-logo { color: #42da9f; font-weight: 800; font-size: 26px; }
                .brand-sub { color: #e05923; font-size: 11px; text-transform: uppercase; }
                .report-title { text-align: center; margin: 30px 0 50px; }
                .report-title h1 { font-size: 28px; font-weight: 800; text-transform: uppercase; color: #0f172a; }
                .summary-word::after { content: ''; position: absolute; bottom: -8px; left: 0; width: 100%; height: 3.5px; background: #f70c0c; }
                .section-title { color: #f70c0c; font-weight: 800; font-size: 17px; border-left: 4px solid #f70c0c; padding-left: 15px; }
                .section-text { font-size: 13px; color: #475569; line-height: 1.8; text-align: justify; }
                .report-table { width: 100%; border-collapse: collapse; margin-top: 20px; border: 1px solid #e2e8f0; border-radius: 12px; overflow: hidden; }
                .report-table th { background: #f8fafc; padding: 14px; font-size: 11px; font-weight: 700; text-transform: uppercase; }
                .report-table td { padding: 13px 10px; font-size: 12px; border-bottom: 1px solid #f1f5f9; text-align: center; }
                .report-table tr { page-break-inside: avoid; page-break-after: auto; }
                .page-footer { margin-top: auto; text-align: center; font-size: 10px; color: #94a3b8; border-top: 1.5px solid #f1f5f9; padding-top: 15px; text-transform: uppercase; letter-spacing: 1px; }
            </style>
        `;
    
        let htmlContent = '';
    
        // Summary Page
        htmlContent += `
            <div class="report-container">
                <div class="exp-page-header">
                    <div>
                        <div class="brand-logo">MedHorizone</div>
                        <div class="brand-sub">Healthcare Tracking System</div>
                    </div>
                    <div style="text-align:right">
                        <div style="font-weight:800">Admin</div>
                        <div>${generatedDate} | ${generatedTime}</div>
                    </div>
                </div>
                <div class="report-title">
                    <h1>BLOOD PRESSURE <span class="summary-word">SUMMARY</span> REPORT</h1>
                </div>
                <div style="margin-bottom:40px">
                    <div class="section-title">1. Executive Summary</div>
                    <p class="section-text">
                        This report provides a comprehensive overview of the blood pressure metrics recorded within the MedHorizone system. The data highlights significant trends in patient cardiovascular health and vital sign management.
                    </p>
                </div>
                <div class="page-footer">
                    Generated by MedHorizone Healthcare System - Confidential Document<br>
                    Page 1 of ${totalPDFPages}
                </div>
            </div>
        `;
    
        // Table Pages
        for (let p = 0; p < totalTablePages; p++) {
            const start = p * rowsPerPage;
            const pageData = data.slice(start, start + rowsPerPage);
    
            htmlContent += `
                <div class="report-container">
                    <div class="exp-page-header">
                        <div>
                            <div class="brand-logo" style="font-size:22px;">MedHorizone</div>
                            <div class="brand-sub">Healthcare Tracking System</div>
                        </div>
                        <div style="text-align:right">
                            <div>${generatedDate}</div>
                        </div>
                    </div>
                    <div style="margin-bottom:20px">
                        <div class="section-title" style="font-size:16px;">
                            Blood Pressure Overview - Page ${p + 1}
                        </div>
                    </div>
                    <table class="report-table">
                        <thead>
                            <tr>
                                <th style="width:5%">#</th>
                                <th style="width:15%">User ID</th>
                                <th style="width:15%">Age/Gender</th>
                                <th style="width:15%">BP Reading</th>
                                <th style="width:15%">Pulse</th>
                                <th style="width:12%">Category</th>
                                <th style="width:13%">Date & Time</th>
                                <th style="width:10%">Device</th>
                            </tr>
                        </thead>
                        <tbody>
                            ${pageData.map((item, idx) => `
                                <tr>
                                    <td>${start + idx + 1}</td>
                                    <td style="font-weight:600;">${item.userId || '—'}</td>
                                    <td>${item.userAge || '—'} / ${item.userGender ? item.userGender.charAt(0).toUpperCase() : '—'}</td>
                                    <td style="font-weight:800;">${item.systolic}/${item.diastolic} <span style="font-size:10px;color:#64748b;">mmHg</span></td>
                                    <td>${item.pulse || '—'} bpm</td>
                                    <td><span style="font-weight: bold;">${item.category}</span></td>
                                    <td>${formatDate(item.testDate)} ${formatTime(item.testDate)}</td>
                                    <td>${item.userDeviceType === 'A' ? 'Android' : item.userDeviceType === 'I' ? 'iOS' : 'Web'}</td>
                                </tr>
                            `).join('')}
                        </tbody>
                    </table>
                    <div class="page-footer">
                        Generated by MedHorizone Healthcare System - Confidential Document<br>
                        Page ${p + 2} of ${totalPDFPages}
                    </div>
                </div>
            `;
        }
    
        const element = document.createElement('div');
        element.innerHTML = styles + htmlContent;
    
        const opt = {
            margin: [10, 15, 10, 15],
            filename: `MedHorizone_Blood_Pressure_Report_${generatedDate.replace(/\//g, '-')}.pdf`,
            image: { type: 'jpeg', quality: 0.98 },
            html2canvas: { scale: 2, useCORS: true, letterRendering: true },
            jsPDF: { unit: 'mm', format: 'a4', orientation: 'portrait' }
        };
    
        html2pdf().set(opt).from(element).save().then(() => {
            setIsExporting(false);
            Swal.fire('Success', 'PDF report generated successfully.', 'success');
        });
    };
     
    const performExport = async (format) => {
        setIsExporting(true);
        try {
            let exportData = [];
            if (exportOption === "currentPage") {
                exportData = reportData.tableData;
            } else if (exportOption === "allPages") {
                const response = await dispatch(fetchBloodPressureReport({
                    ...filters,
                    search: debouncedSearch,
                    page: 1,
                    limit: 10000 
                }));
                if (response.payload && response.payload.success) {
                    exportData = response.payload.tableData;
                }
            }

            if (!exportData || exportData.length === 0) {
                Swal.fire("No Data", "No data found for the selected range.", "warning");
            } else {
                if (format === "csv") generateCSV(exportData);
                else generatePDF(exportData);
            }
        } catch (err) {
            console.error("Export failed:", err);
            Swal.fire("Error", "Failed to export data.", "error");
        } finally {
            setIsExporting(false);
            setShowExportModal(false);
        }
    };

    // Shared Styles
    const filterGroupStyle = { display: 'flex', flexDirection: 'column', gap: '6px', flex: '1' };
    const filterLabelStyle = { fontSize: '11px', fontWeight: '700', color: '#414347', textTransform: 'uppercase', letterSpacing: '0.8px', marginBottom: '2px' };
    const inputWrapperStyle = { position: 'relative', display: 'flex', alignItems: 'center' };
    const iconStyle = { position: 'absolute', left: '10px', fontSize: '14px', color: '#94a3b8', pointerEvents: 'none' };
    const modernInputStyle = { width: '100%', padding: '9px 12px 9px 32px', border: '1px solid #e2e8f0', borderRadius: '8px', fontSize: '13px', color: '#1e293b', background: '#f8fafc', transition: 'all 0.2s', outline: 'none' };

    const handleSortChange = (value) => {
        setSortBy(value);
        setCurrentPage(1);
    };

    const renderSortHeader = (label, field, width) => (
        <th className="sticky-header-th" style={{ width }}>
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '8px' }}>
                <span>{label}</span>
                <span style={{ display: 'flex', flexDirection: 'row', alignItems: 'center', cursor: 'pointer', fontSize: '11px', gap: '4px' }}>
                    <FaChevronUp color={sortBy === `${field}:asc` ? '#10b981' : '#cbd5e1'} onClick={() => handleSortChange(`${field}:asc`)} />
                    <FaChevronDown color={sortBy === `${field}:desc` ? '#10b981' : '#cbd5e1'} onClick={() => handleSortChange(`${field}:desc`)} />
                </span>
            </div>
        </th>
    );

    return (
        <div style={{ padding: '24px', background: '#f5f7fa', minHeight: '100vh', fontFamily: 'Inter, sans-serif' }}>
            <div style={{ marginBottom: '24px', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <div>
                    <h4 style={{ margin: 0, fontWeight: 700, color: '#1a1a1a', fontSize: '24px' }}>Blood Pressure Analysis</h4>
                </div>
            </div>

            {/* Summary Cards */}
            <div style={{ display: 'flex', gap: '16px', flexWrap: 'wrap', marginBottom: '32px' }}>
                {[
                    { title: 'Total Readings', value: reportData.summary.totalReadings, iconColor: '#1e40af', background: '#eff6ff' },
                    { title: 'Avg Systolic', value: reportData.summary.avgSystolic !== null ? `${reportData.summary.avgSystolic} mmHg` : null, iconColor: '#065f46', background: '#ecfdf5' },
                    { title: 'Avg Diastolic', value: reportData.summary.avgDiastolic !== null ? `${reportData.summary.avgDiastolic} mmHg` : null, iconColor: '#991b1b', background: '#fef2f2' },
                    { title: 'Avg Pulse', value: reportData.summary.avgPulse !== null ? `${reportData.summary.avgPulse} bpm` : null, iconColor: '#6b21a8', background: '#f3e8ff' }
                ].map((stat, i) => (
                    <div key={i} style={{ flex: '1', minWidth: '220px', background: stat.background, borderRadius: '12px', padding: '20px', boxShadow: '0 2px 8px rgba(0,0,0,0.08)', textAlign: 'center', minHeight: '120px', display: 'flex', flexDirection: 'column', justifyContent: 'center' }}>
                        <div style={{ fontSize: '14px', color: '#64748b', fontWeight: 500 }}>{stat.title}</div>
                        <div style={{ fontSize: '28px', fontWeight: 700, color: stat.iconColor, margin: '8px 0' }}>
                            {stat.value !== null ? stat.value : <ComponentLoader color={stat.iconColor} size="24px" padding="0" />}
                        </div>
                    </div>
                ))}
            </div>

            {/* Filter Section Card */}
            <Card style={{ border: 'none', borderRadius: '12px', boxShadow: '0 1px 3px rgba(0,0,0,0.1)', marginBottom: '16px', overflow: 'hidden' }}>
                <Card.Body style={{ padding: '20px' }}>
                    <div style={{ display: 'flex', gap: '12px', alignItems: 'center', marginBottom: '20px', flexWrap: 'wrap' }}>
                        <span style={{ fontWeight: 600, color: '#374151', fontSize: '14px' }}>Reading Date Range:</span>
                        <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                            <Form.Control
                                type="date"
                                value={filters.startDate}
                                onChange={(e) => handleFilterChange('startDate', e.target.value)}
                                style={{ padding: '8px 12px', border: '1px solid #d1d5db', borderRadius: '8px', fontSize: '13px', color: '#374151', background: '#f9fafb', width: '150px' }}
                            />
                            <span style={{ color: '#9ca3af', fontWeight: 500, fontSize: '14px' }}>to</span>
                            <Form.Control
                                type="date"
                                value={filters.endDate}
                                min={filters.startDate}
                                onChange={(e) => handleFilterChange('endDate', e.target.value)}
                                style={{ padding: '8px 12px', border: '1px solid #d1d5db', borderRadius: '8px', fontSize: '13px', color: '#374151', background: '#f9fafb', width: '150px' }}
                            />
                        </div>
                        <button
                            onClick={clearFilters}
                            style={{
                                padding: '8px 16px', background: '#ef4444', color: 'white', border: 'none', borderRadius: '8px', 
                                fontWeight: 700, fontSize: '12px', display: 'flex', alignItems: 'center', gap: '6px', cursor: 'pointer', textTransform: 'uppercase'
                            }}
                        >
                            <FaTimes /> CLEAR FILTERS
                        </button>
                        <span style={{
                            marginLeft: 'auto', padding: '6px 16px', background: '#f1f5f9', color: '#4f46e5', borderRadius: '20px', fontSize: '14px', fontWeight: 700
                        }}>
                            Total Records : {totalCount}
                        </span>
                    </div>

                    <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(130px, 1fr))', gap: '16px', borderTop: '1px solid #f1f5f9', paddingTop: '16px' }}>
                        <div style={filterGroupStyle}>
                            <label style={filterLabelStyle}>USER ID</label>
                            <div style={inputWrapperStyle}>
                                <FaSearch style={iconStyle} />
                                <input type="text" placeholder="Search..." value={filters.searchTerm} onChange={handleSearchChange} style={modernInputStyle}/>
                            </div>
                        </div>

                        <div style={filterGroupStyle}>
                            <label style={filterLabelStyle}>AGE</label>
                            <div style={inputWrapperStyle}>
                                <FaUser style={iconStyle} />
                                <Form.Select value={filters.ageRange} onChange={(e) => handleFilterChange('ageRange', e.target.value)} style={modernInputStyle}>
                                    <option>All</option>
                                    <option>1-18</option>
                                    <option>19-25</option>
                                    <option>26-35</option>
                                    <option>36-45</option>
                                    <option>46-60</option>
                                    <option>60+</option>
                                </Form.Select>
                            </div>
                        </div>

                        <div style={filterGroupStyle}>
                            <label style={filterLabelStyle}>GENDER</label>
                            <div style={inputWrapperStyle}>
                                <FaVenusMars style={iconStyle} />
                                <Form.Select value={filters.gender} onChange={(e) => handleFilterChange('gender', e.target.value)} style={modernInputStyle}>
                                    <option>All</option>
                                    <option>Male</option>
                                    <option>Female</option>
                                    <option>Others</option>
                                </Form.Select>
                            </div>
                        </div>

                        <div style={filterGroupStyle}>
                            <label style={filterLabelStyle}>DEVICE</label>
                            <div style={inputWrapperStyle}>
                                <FaMobileAlt style={iconStyle} />
                                <Form.Select value={filters.deviceType} onChange={(e) => handleFilterChange('deviceType', e.target.value)} style={modernInputStyle}>
                                    <option>All</option>
                                    <option>Android</option>
                                    <option>iOS</option>
                                </Form.Select>
                            </div>
                        </div>

                        <div style={filterGroupStyle}>
                            <label style={filterLabelStyle}>CATEGORY</label>
                            <div style={inputWrapperStyle}>
                                <FaLayerGroup style={iconStyle} />
                                <Form.Select value={filters.category} onChange={(e) => handleFilterChange('category', e.target.value)} style={modernInputStyle}>
                                    <option>All</option>
                                    <option>Normal</option>
                                    <option>Elevated</option>
                                    <option>Stage 1</option>
                                    <option>Stage 2</option>
                                    <option>Crisis</option>
                                    <option>Low</option>
                                </Form.Select>
                            </div>
                        </div>

                        <div style={filterGroupStyle}>
                            <label style={filterLabelStyle}>SYSTOLIC</label>
                            <div style={inputWrapperStyle}>
                                <FaDotCircle style={iconStyle} />
                                <Form.Select value={filters.systolicRange} onChange={(e) => handleFilterChange('systolicRange', e.target.value)} style={modernInputStyle}>
                                    <option>All</option>
                                    <option>&lt; 90</option>
                                    <option>90-119</option>
                                    <option>120-129</option>
                                    <option>130-139</option>
                                    <option>140-179</option>
                                    <option>&gt;= 180</option>
                                </Form.Select>
                            </div>
                        </div>

                        <div style={filterGroupStyle}>
                            <label style={filterLabelStyle}>DIASTOLIC</label>
                            <div style={inputWrapperStyle}>
                                <FaDotCircle style={iconStyle} />
                                <Form.Select value={filters.diastolicRange} onChange={(e) => handleFilterChange('diastolicRange', e.target.value)} style={modernInputStyle}>
                                    <option>All</option>
                                    <option>&lt; 60</option>
                                    <option>60-79</option>
                                    <option>80-89</option>
                                    <option>90-119</option>
                                    <option>&gt;= 120</option>
                                </Form.Select>
                            </div>
                        </div>
                    </div>
                </Card.Body>
            </Card>

            {/* Charts Row */}
            <div style={{ display: 'flex', gap: '24px', flexWrap: 'wrap', marginBottom: '32px' }}>
                <div style={{ flex: '2', minWidth: '600px', background: '#fff', borderRadius: '12px', padding: '24px', boxShadow: '0 2px 8px rgba(0,0,0,0.08)' }}>
                    <h5 style={{ fontSize: '16px', fontWeight: 650, marginBottom: '20px' }}>BP Distribution by Gender</h5>
                    <ResponsiveContainer width="100%" height={320}>
                        <BarChart data={reportData.barChartData} margin={{ top: 20, right: 30, left: 20, bottom: 5 }}>
                            <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="#f3f4f6" />
                            <XAxis dataKey="name" axisLine={false} tickLine={false} tick={{ fontSize: 12, fill: '#6b7280' }} />
                            <YAxis axisLine={false} tickLine={false} tick={{ fontSize: 12, fill: '#6b7280' }} />
                            <Tooltip contentStyle={{ borderRadius: '8px', border: 'none', boxShadow: '0 4px 12px rgba(0,0,0,0.1)' }} />
                            <Legend verticalAlign="top" align="right" height={36} iconType="circle" />
                            <Bar dataKey="Male" fill="#4f46e5" radius={[4, 4, 0, 0]} barSize={14} />
                            <Bar dataKey="Female" fill="#ec4899" radius={[4, 4, 0, 0]} barSize={14} />
                            <Bar dataKey="Other" fill="#94a3b8" radius={[4, 4, 0, 0]} barSize={14} />
                        </BarChart>
                    </ResponsiveContainer>
                </div>

                <div style={{ flex: '1', minWidth: '350px', background: '#fff', borderRadius: '12px', padding: '24px', boxShadow: '0 2px 8px rgba(0,0,0,0.08)' }}>
                    <h6 style={{ fontSize: '14px', fontWeight: 700, color: '#1a1a1a', marginBottom: '20px' }}>Reading Categories</h6>
                    <div style={{ display: 'flex', flexDirection: 'column', gap: '20px' }}>
                        {['Normal', 'Elevated', 'Stage 1', 'Stage 2', 'Crisis', 'Low'].map((cat, i) => {
                            const chartItem = reportData.barChartData.find(d => d.name === cat);
                            const count = chartItem ? (chartItem.Male + chartItem.Female + chartItem.Other) : 0;
                            const total = reportData.summary.totalReadings || 1;
                            const p = Math.round((count / total) * 100);
                            const colors = { Normal: '#10b981', 'Stage 1': '#f59e0b', 'Stage 2': '#ef4444', Elevated: '#3b82f6', Crisis: '#be123c', Low: '#6b7280' };
                            return (
                                <div key={i}>
                                    <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '6px' }}>
                                        <span style={{ fontSize: '12px', fontWeight: 600, color: '#64748b' }}>{cat}</span>
                                        <span style={{ fontSize: '12px', fontWeight: 700 }}>{p}%</span>
                                    </div>
                                    <div style={{ width: '100%', height: '8px', background: '#f1f5f9', borderRadius: '4px' }}>
                                        <div style={{ width: `${p}%`, height: '100%', background: colors[cat], borderRadius: '4px' }}></div>
                                    </div>
                                </div>
                            );
                        })}
                    </div>
                </div>
            </div>

            {/* Table Section */}
            <div style={{ background: 'white', borderRadius: '12px', boxShadow: '0 1px 3px rgba(0,0,0,0.1)', overflow: 'hidden' }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '16px 24px', borderBottom: '1px solid #e5e7eb' }}>
                    <h5 style={{ margin: 0, fontSize: '18px', fontWeight: 700, color: '#1a1a1a' }}>Blood Pressure Readings</h5>
                    <div style={{ display: 'flex', gap: '12px', alignItems: 'center' }}>
                         <Form.Select
                            value={itemsPerPage}
                            onChange={(e) => handleItemsPerPageChange(e.target.value)}
                            style={{ width: '130px', height: '38px', fontSize: '13px', borderRadius: '8px', border: '1px solid #e2e8f0' }}
                        >
                            <option value={10}>10 records</option>
                            <option value={15}>15 records</option>
                            <option value={20}>20 records</option>
                            <option value={25}>25 records</option>
                            <option value={30}>30 records</option>
                            <option value={50}>50 records</option>
                            <option value={100}>100 records</option>
                        </Form.Select>

                        {/* <button
                            onClick={() => setShowExportModal(true)}
                            style={{
                                padding: '8px 16px', background: '#10b981', color: 'white', border: 'none', borderRadius: '8px', 
                                fontWeight: 700, fontSize: '12px', display: 'flex', alignItems: 'center', gap: '8px', cursor: 'pointer', height: '38px'
                            }}
                        >
                            <FaDownload /> EXPORT
                        </button> */}
                    </div>
                </div>

                <style>{`
                .sticky-table {
                    border-collapse: separate !important;
                    border-spacing: 0 !important;
                    overflow: visible !important;
                    position: relative !important;
                }
                .sticky-header-th {
                    position: sticky !important;
                    top: -1px !important;
                    z-index: 11 !important;
                    background: #f9fafb !important;
                    border-bottom: 1px solid #e5e7eb !important;
                }
                /* Ensure parent containers don't clip the sticky header */
                .scroll-container {
                    max-height: 600px;
                    overflow-y: auto !important;
                    overflow-x: auto !important;
                    position: relative !important;
                }
            `}</style>

                <div className="scroll-container">
                    {loading && (
                        <div style={{ position: 'absolute', inset: 0, background: 'rgba(255,255,255,0.7)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 20 }}>
                            <ComponentLoader color="#4f46e5" size="40px" />
                        </div>
                    )}
                    <Table hover className="sticky-table" style={{ margin: 0, tableLayout: 'fixed', minWidth: '1000px' }}>
                        <thead>
                            <tr>
                                <th className="sticky-header-th" style={{ width: '5%' }}>#</th>
                                {renderSortHeader('User ID', 'userId', '20%')}
                                {renderSortHeader('Gender', 'userGender', '8%')}
                                {renderSortHeader('Age', 'userAge', '5%')}
                                {renderSortHeader('BP Reading', 'systolic', '12%')}
                                {renderSortHeader('Pulse', 'pulse', '10%')}
                                {renderSortHeader('Category', 'category', '10%')}
                                {renderSortHeader('Date & Time', 'testDate', '20%')}
                                {renderSortHeader('Device', 'userDeviceType', '10%')}
                            </tr>
                        </thead>
                        <tbody>
                            {reportData.tableData.map((item, index) => (
                                <tr key={index}>
                                    <td style={{ textAlign: 'center', verticalAlign: 'middle', color: '#64748b' }}>{(currentPage - 1) * itemsPerPage + index + 1}</td>
                                    <td style={{ verticalAlign: 'middle', textAlign: 'center' }}>
                                        <div style={{ fontWeight: 600, color: '#4f46e5', fontSize: '14px' }}>{item.userId}</div>
                                    </td>
                                    <td style={{ textAlign: 'center', verticalAlign: 'middle' }}>
                                        {item.userGender ? item.userGender.charAt(0).toUpperCase() + item.userGender.slice(1).toLowerCase() : "—"}
                                    </td>
                                    <td style={{ textAlign: 'center', verticalAlign: 'middle' }}>{item.userAge ?? '—'}</td>
                                    <td style={{ textAlign: 'center', verticalAlign: 'middle' }}>
                                        <div style={{ fontSize: '15px', fontWeight: 700, color: '#1e293b' }}>{item.systolic} / {item.diastolic} <span style={{ fontSize: '10px', color: '#94a3b8', fontWeight: 500 }}>mmHg</span></div>
                                    </td>
                                    <td style={{ textAlign: 'center', verticalAlign: 'middle' }}>
                                        <div style={{ fontSize: '14px', color: '#ef4444', fontWeight: 700 }}>
                                            <FaHeartbeat style={{ marginRight: '4px' }} />
                                            {item.pulse}
                                            <span style={{ fontSize: '10px', color: '#94a3b8', marginLeft: '4px', fontWeight: 500 }}>BPM</span>
                                        </div>
                                    </td>
                                    <td style={{ textAlign: 'center', verticalAlign: 'middle' }}>
                                        <span style={{
                                            padding: '4px 12px', borderRadius: '20px', fontSize: '10px', fontWeight: 700, textTransform: 'uppercase',
                                            background: item.category === 'Normal' ? '#d1fae5' : 
                                                        item.category === 'Elevated' ? '#eff6ff' :
                                                        item.category === 'Stage 1' ? '#fff7ed' :
                                                        item.category === 'Stage 2' ? '#ffedd5' :
                                                        item.category === 'Crisis' ? '#fee2e2' : 
                                                        item.category === 'Low' ? '#f1f5f9' : '#f8fafc',
                                            color: item.category === 'Normal' ? '#065f46' : 
                                                   item.category === 'Elevated' ? '#1e40af' :
                                                   item.category === 'Stage 1' ? '#c2410c' :
                                                   item.category === 'Stage 2' ? '#9a3412' :
                                                   item.category === 'Crisis' ? '#991b1b' : 
                                                   item.category === 'Low' ? '#64748b' : '#475569'
                                        }}>
                                            {item.category}
                                        </span>
                                    </td>
                                    <td style={{ textAlign: 'center', verticalAlign: 'middle' }}>
                                        <div style={{ fontSize: '13px', fontWeight: 600 }}>{formatDate(item.testDate)}</div>
                                        <div style={{ fontSize: '11px', color: '#94a3b8' }}>{formatTime(item.testDate)}</div>
                                    </td>
                                    <td style={{ textAlign: 'center', verticalAlign: 'middle' }}>
                                        <span style={{
                                            padding: '4px 10px', borderRadius: '20px', fontSize: '11px', fontWeight: 600,
                                            background: item.userDeviceType === 'A' ? '#ecfdf5' : item.userDeviceType === 'I' ? '#eff6ff' : '#f1f5f9',
                                            color: item.userDeviceType === 'A' ? '#065f46' : item.userDeviceType === 'I' ? '#1e40af' : '#64748b'
                                        }}>
                                            {item.userDeviceType === 'A' ? 'Android' : item.userDeviceType === 'I' ? 'iOS' : 'Web'}
                                        </span>
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </Table>
                </div>

                <div className="modern-pagination-wrapper">
                    <div className="pagination-left">
                        <div className="d-flex align-items-center gap-2">
                            <span className="pagination-label">Go to:</span>
                            <input type="number" className="pagination-input-box" value={goToPage} onChange={(e) => setGoToPage(e.target.value)} onKeyPress={(e) => e.key === 'Enter' && handleGoToPage()} />
                            <button className="pagination-go-button" onClick={handleGoToPage}>Go</button>
                        </div>
                        <span className="pagination-label">Showing {(currentPage - 1) * itemsPerPage + 1} to {Math.min(currentPage * itemsPerPage, totalCount)} of {totalCount} entries</span>
                    </div>
                    <div className="pagination-right">
                        <div className="ms-3 d-flex gap-2">
                            <button 
                                className="pagination-nav-btn" 
                                onClick={() => setCurrentPage(1)} 
                                disabled={currentPage === 1}
                            >
                                First
                            </button>
                            <button 
                                className="pagination-nav-btn" 
                                onClick={() => setCurrentPage(prev => Math.max(1, prev - 1))} 
                                disabled={currentPage === 1}
                            >
                                Prev
                            </button>
                            <button 
                                className="pagination-nav-btn" 
                                onClick={() => setCurrentPage(prev => prev + 1)} 
                                disabled={currentPage * itemsPerPage >= totalCount}
                            >
                                Next
                            </button>
                            <button 
                                className="pagination-nav-btn" 
                                onClick={() => setCurrentPage(Math.ceil(totalCount / itemsPerPage))} 
                                disabled={currentPage * itemsPerPage >= totalCount}
                            >
                                Last
                            </button>
                        </div>
                    </div>
                </div>
            </div>

            <Modal show={showExportModal} onHide={() => setShowExportModal(false)} centered>
                <Modal.Header closeButton style={{ background: '#f8fafc', borderBottom: '1px solid #e2e8f0' }}>
                    <Modal.Title style={{ fontSize: '18px', fontWeight: 700, color: '#1e293b' }}>Export Blood Pressure Report</Modal.Title>
                </Modal.Header>
                <Modal.Body style={{ padding: '24px' }}>
                    <div style={{ marginBottom: '20px' }}>
                        <label style={{ display: 'block', fontSize: '13px', fontWeight: 600, color: '#64748b', marginBottom: '10px' }}>SELECT DATA RANGE</label>
                        <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
                            <Form.Check type="radio" label={`Current Page(${reportData.tableData.length} records)`} name="exportOption" checked={exportOption === "currentPage"} onChange={() => setExportOption("currentPage")} style={{ fontSize: '14px', color: '#1e293b' }} />
                            <Form.Check type="radio" label={`All Pages(${totalCount} records)`} name="exportOption" checked={exportOption === "allPages"} onChange={() => setExportOption("allPages")} style={{ fontSize: '14px', color: '#1e293b' }} />
                        </div>
                    </div>
                    <div style={{ display: 'flex', gap: '12px', marginTop: '30px' }}>
                        <Button variant="primary" disabled={isExporting} onClick={() => performExport("csv")} style={{ flex: 1, background: '#4f46e5', border: 'none', fontWeight: 600, height: '40px' }}>{isExporting ? "Processing..." : "Export as CSV"}</Button>
                        <Button variant="danger" disabled={isExporting} onClick={() => performExport("pdf")} style={{ flex: 1, background: '#ef4444', border: 'none', fontWeight: 600, height: '40px' }}>{isExporting ? "Processing..." : "Export as PDF"}</Button>
                    </div>
                </Modal.Body>
            </Modal>
        </div>
    );
};

export default BloodPressureReport;
